import type { DxfEntityKey } from '@frontend/api';
import { type ChunkRef, chunkKey } from '@frontend/viewer-core';

import type { DrawingModel, MappingAssignments } from './types';

/**
 * The seam through which loop-granularity **selection** chaining reads a
 * mapping. `viewer-core`'s `walkChain` is handed `chainKeyOf` and never
 * learns there are two implementations of it.
 *
 * That is deliberate, and specifically for the transition the DXF pipeline is
 * in: today a file may or may not carry a mapping, and either is normal.
 * Once scenes are auto-mapped this seam does not move — it is still "which
 * policy does this session hold", just always the mapping-backed one. And a
 * context that wants role-less rendering regardless of what a scene carries
 * can keep asking for the null policy on purpose, rather than that being a
 * side effect of "no mapping happened to be saved yet".
 */
export type RolePolicy = {
  /**
   * Grouping key for loop-granularity selection chaining.
   *
   * Two chunks chain when their keys are equal; `null` never chains with
   * anything, not even another `null` — that's what stops a chain running
   * through hidden geometry. See `walkChain`'s `keyOf` for the full contract.
   */
  chainKeyOf(chunk: ChunkRef): string | null;
};

/** Canonical form of a role set, so `{etch, bend}` and `{bend, etch}` match. */
const rolesKey = (roles: readonly string[] | undefined): string =>
  roles === undefined || roles.length === 0 ? '' : [...roles].sort().join(',');

export type RolePolicyOptions = {
  readonly model: DrawingModel;
  readonly assignments: MappingAssignments | null;
  readonly hiddenLayers: ReadonlySet<number>;
};

/**
 * The role-aware policy: a mapping is in force, and `chainKeyOf` reads it.
 *
 * `assignments` is a real `MappingAssignments`, not `| null` — a caller with
 * no mapping wants `createNullRolePolicy` instead, so this never has to ask
 * "what if there's no mapping" on every lookup.
 */
export const createMappingRolePolicy = ({
  model,
  assignments,
  hiddenLayers,
}: {
  model: DrawingModel;
  assignments: MappingAssignments;
  hiddenLayers: ReadonlySet<number>;
}): RolePolicy => {
  // Memoised per policy instance. A chain re-reads the same neighbours from
  // several nodes, and each miss costs a loop lookup plus a sort.
  const cache = new Map<string, string | null>();

  return {
    chainKeyOf(chunk) {
      const cacheKey = chunkKey(chunk);
      const cached = cache.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
      const entity = model.loopsById.get(chunk.loopId)?.chunks[
        chunk.chunkIndex
      ];
      const key =
        entity === undefined || hiddenLayers.has(entity.layer)
          ? null
          : rolesKey(assignments.get(entity.entityId));
      cache.set(cacheKey, key);
      return key;
    },
  };
};

/**
 * The role-less policy: nothing carries a role, because there is nothing to
 * read one from — either the file has no mapping, or the caller wants a
 * role-less view of one that does.
 *
 * Still has to mask hidden layers even though it never consults a role, so
 * loop chaining keeps refusing to run through geometry the user switched
 * off, exactly as it does under a mapping.
 */
export const createNullRolePolicy = ({
  model,
  hiddenLayers,
}: {
  model: DrawingModel;
  hiddenLayers: ReadonlySet<number>;
}): RolePolicy => ({
  chainKeyOf(chunk) {
    const entity = model.loopsById.get(chunk.loopId)?.chunks[chunk.chunkIndex];
    return entity === undefined || hiddenLayers.has(entity.layer) ? null : '';
  },
});

/**
 * Picks the policy for the mapping in force: the null policy when `assignments`
 * is `null` (no mapping row exists / not editing), the mapping-backed one
 * otherwise.
 *
 * This is the one place "what does no mapping mean" gets decided for
 * selection chaining — everything downstream just holds whichever
 * `RolePolicy` this returns and never asks the question itself.
 */
export const createRolePolicy = ({
  model,
  assignments,
  hiddenLayers,
}: RolePolicyOptions): RolePolicy =>
  assignments === null
    ? createNullRolePolicy({ model, hiddenLayers })
    : createMappingRolePolicy({ model, assignments, hiddenLayers });

/**
 * Reuses one `createRolePolicy` result across calls instead of rebuilding it
 * (and throwing away its per-chunk cache) every time.
 *
 * Hover re-picks and marquee drags both ask for a policy on every relevant
 * tick, but `model`, `assignments`, and `hiddenLayers` are normally the same
 * three references from one call to the next.
 */
export const createMemoizedRolePolicy = (): ((
  options: RolePolicyOptions,
) => RolePolicy) => {
  let cached: (RolePolicyOptions & { policy: RolePolicy }) | null = null;

  return (options) => {
    if (
      cached &&
      cached.model === options.model &&
      cached.assignments === options.assignments &&
      cached.hiddenLayers === options.hiddenLayers
    ) {
      return cached.policy;
    }
    const policy = createRolePolicy(options);
    cached = { ...options, policy };
    return policy;
  };
};

/**
 * The seam through which the material-fill contour reads "is this entity
 * cutting". Its own interface rather than folded into `RolePolicy`: fill
 * traces faces of the arrangement of cutting chunks (angular turn at each
 * welded node) rather than chaining by `chainKeyOf`. A linear walk that
 * crosses from one stitched loop into another via a shared node can close a
 * polygon that does not correspond to any real enclosed region; the
 * arrangement walk cannot, because it never invents a chord and refuses a
 * turn it cannot uniquely order.
 */
export type CuttingClassifier = {
  isCutting(entityId: DxfEntityKey): boolean;
};

/** The role-aware classifier: `cuttingKeys` is exactly which entities carry
 *  the cutting role — see `RoleIndex`. */
export const createCuttingClassifier = (
  cuttingKeys: ReadonlySet<DxfEntityKey>,
): CuttingClassifier => ({
  isCutting: (entityId) => cuttingKeys.has(entityId),
});

/**
 * The role-less classifier: **nothing is cutting**. The current default for
 * a file with no mapping, and for any context that wants a role-less fill on
 * purpose — chosen once here rather than scattered as a `?? someSet` at every
 * call site. Change it here to change it everywhere this classifier is used.
 */
export const NULL_CUTTING_CLASSIFIER: CuttingClassifier = {
  isCutting: () => false,
};

/**
 * Picks the classifier for the mapping in force, memoised so the ring cache
 * downstream (`createMaterialFillBuilder`) sees the *same* classifier
 * reference across an edit that never touches the cutting role.
 *
 * That reference stability is why this reads `cuttingKeys` rather than
 * `assignments` directly: `roleIndex.get(CUTTING)` keeps its previous `Set`
 * reference whenever an edit leaves the cutting role untouched (see
 * `mappingController.roleIndex`), while `assignments` itself is a new
 * reference on *every* edit. Keying on `assignments` here would rebuild —
 * and force every downstream cache to rebuild — on an edit that only ever
 * touched some other role.
 */
export const createMemoizedCuttingClassifier = (): ((options: {
  hasMapping: boolean;
  cuttingKeys: ReadonlySet<DxfEntityKey>;
}) => CuttingClassifier) => {
  let cached: {
    hasMapping: boolean;
    cuttingKeys: ReadonlySet<DxfEntityKey>;
    classifier: CuttingClassifier;
  } | null = null;

  return ({ hasMapping, cuttingKeys }) => {
    if (
      cached &&
      cached.hasMapping === hasMapping &&
      cached.cuttingKeys === cuttingKeys
    ) {
      return cached.classifier;
    }
    const classifier = hasMapping
      ? createCuttingClassifier(cuttingKeys)
      : NULL_CUTTING_CLASSIFIER;
    cached = { hasMapping, cuttingKeys, classifier };
    return classifier;
  };
};
